import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';

import { useParams, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { useInView } from 'react-intersection-observer';
import { Helmet } from 'react-helmet';

import { Discount } from 'app/types';

import { ExploreState, setFromExplore } from 'features/explore/exploreSlice';

import Text from 'common/components/Text/Text';
import Loader from 'common/components/Loader/Loader';
import { TabsComponent } from 'features/explore/program/tabs/Tabs';
import { SessionState } from 'features/session/sessionSlice';

import {
  useGetProgramQuery,
  useAddFavoriteMutation,
  useDeleteFavoriteMutation,
  useAddPostulationMutation,
} from 'services/exploreApi';

import { detailUrl, newDetailUrl } from 'common/utils/conversions';
import { navigateTop, smoothScroll } from 'common/utils';
import { useAppDispatch, useAppSelector } from 'app/hooks';

import styles from './ProgramDetail.module.scss';
import textStyles from 'common/styles/text.module.scss';
import Placeholder from 'assets/explore/placeholder.png';
import {
  setFavorites,
  UserState,
  setSchoolShortlisted,
  setApplicationStarted,
  setLastStepDone,
} from 'features/user/userSlice';

import Flag from 'react-world-flags';
import { OverviewComponent } from '../common/components/Overview';
import { MatchingSection } from '../common/components/MatchingSection';
import { TagsSection } from '../common/components/TagsSection';
import { ContactBanner } from '../common/components/ContactBanner';
import { ProfilingBanner } from '../common/components/ProfilingBanner';
import { ProgramProfileSection } from './tabs/ProgramProfileTab';
import { TeachingSection } from 'features/explore/common/components/TeachingSection';
import { ApplySection, JourneyBanner } from '../common/components/ApplySection';
import { CostsSection } from './tabs/CostsTab';
import { CareerSection } from '../common/components/CareerSection';
import { VisaSection } from '../common/components/VisaSection';
import StudentLife from '../common/components/StudentLifeSection';
import { FAQSection } from '../common/components/FaqSection';
import SimilarPrograms from './tabs/SimilarProgramsTab';
import { InternationalStudentsComponent } from '../common/components/InternationalStudentSection';
import { DetailSections } from '../common/components/DetailSections';

const ProgramDetail: FunctionComponent = () => {
  const { logged } = useAppSelector(({ session }: { session: SessionState }) => session);
  const { programSlug } = useParams<{ programSlug: string }>();
  const [programId] = programSlug?.split('--') || [];
  const {
    data: program,
    isLoading,
    isFetching,
  } = useGetProgramQuery({
    program_id: parseInt(programId || ''),
  });
  // const [selectedCampus, setSelectedCampus] = useState<number>(0);
  const [isFavorite, setFavorite] = useState<boolean>(
    program?.data.favorite ? program.data.favorite : false
  );
  const [activeProgramTopic, setActiveProgramTopic] = useState<number>(0);
  const { favorites } = useAppSelector(({ user }: { user: UserState }) => user);
  const dispatch = useAppDispatch();
  const [addFavorite] = useAddFavoriteMutation();
  const [deleteFavorite] = useDeleteFavoriteMutation();
  const [submitPostulation] = useAddPostulationMutation();
  const handleApply = () => {
    const values = { program_id: program?.data.id };
    if (logged) {
      if (program?.data.postulated) {
        navigate(`/home/applications/${program?.data.postulation_id}`);
      } else {
        submitPostulation(values).then((resp: any) => {
          dispatch(setApplicationStarted(true));
          dispatch(setLastStepDone(6));
          navigate(`/home/applications/${resp.data.data.id}`);
        });
      }
    } else {
      setTimeout(() => {
        navigate(`/signup?apply_pid=${program?.data.id}`);
      }, 100);
    }
  };
  const handleAddFavorite = () => {
    const values = { id: program?.data.id, kind: 'Program' };
    if (logged) {
      addFavorite(values).then((resp: any) => {
        setFavorite(!isFavorite);
        if (program && !favorites?.includes(program.data.id)) {
          dispatch(setFavorites([...(favorites || []), program.data.id]));
        }
        dispatch(setSchoolShortlisted(true));
        dispatch(setLastStepDone(5));
      });
    } else {
      setTimeout(() => {
        navigate('/signup');
      }, 100);
    }
  };
  const handleDeleteFavorite = () => {
    const values = { id: program?.data.id, kind: 'Program' };
    deleteFavorite(values).then((resp: any) => {
      setFavorite(!isFavorite);
      dispatch(setFavorites(favorites?.filter((id: number) => id !== program?.data.id)));
    });
  };

  const { comesFromExplore } = useAppSelector(({ explore }: { explore: ExploreState }) => explore);
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const url = detailUrl('programs', program?.data.id, program?.data.name);

  const { ref: headerRef, inView: headerInView } = useInView({
    threshold: 1,
  });

  const overviewRef = useRef(null);
  const programProfileRef = useRef(null);
  const applyRef = useRef(null);
  const costsRef = useRef(null);
  const careerRef = useRef(null);
  const visaRef = useRef(null);
  const studentLifeRef = useRef(null);
  const faqRef = useRef(null);
  const similarProgramsRef = useRef(null);
  const schoolOverviewRef = useRef(null);

  dispatch(setFromExplore(location.pathname.includes('explore')));

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const element = entry.target;
      let activeTopic = 0;
      if (element.id === 'overview') activeTopic = 0;
      if (element.id === 'program-profile') activeTopic = 1;
      if (element.id === 'apply') activeTopic = 2;
      if (element.id === 'fee-costs') activeTopic = 3;
      if (element.id === 'career') activeTopic = 4;
      if (element.id === 'visa') activeTopic = 5;
      if (element.id === 'student-life') activeTopic = 6;
      if (element.id === 'faq') activeTopic = 7;
      if (element.id === 'similar-programs') activeTopic = 8;
      if (element.id === 'school-overview') activeTopic = 9;
      if (entry.isIntersecting && activeTopic != undefined) {
        setActiveProgramTopic(activeTopic);
      }
    });
  });

  useEffect(() => {
    if (overviewRef.current) observer.observe(overviewRef.current);
    if (programProfileRef.current) observer.observe(programProfileRef.current);
    if (applyRef.current) observer.observe(applyRef.current);
    if (costsRef.current) observer.observe(costsRef.current);
    if (careerRef.current) observer.observe(careerRef.current);
    if (visaRef.current) observer.observe(visaRef.current);
    if (studentLifeRef.current) observer.observe(studentLifeRef.current);
    if (faqRef.current) observer.observe(faqRef.current);
    if (similarProgramsRef.current) observer.observe(similarProgramsRef.current);
    if (schoolOverviewRef.current) observer.observe(schoolOverviewRef.current);
  }, [
    overviewRef.current,
    programProfileRef.current,
    applyRef.current,
    costsRef.current,
    careerRef.current,
    visaRef.current,
    studentLifeRef.current,
    faqRef.current,
    similarProgramsRef.current,
    schoolOverviewRef.current,
    observer,
  ]);

  useEffect(() => {
    if (program?.data.name) document.title = `Unify | ${program?.data.name}`;
    if (favorites?.includes(program ? program.data.id : -1) && logged) {
      setFavorite(true);
    } else {
      setFavorite(false);
    }
  }, [program?.data.name]);

  useEffect(() => {
    if (favorites?.includes(program ? program.data.id : -1) && logged) {
      setFavorite(true);
    } else {
      setFavorite(false);
    }
  }, [favorites]);

  const navigateTo = (route: string) => {
    navigateTop();
    dispatch(setFromExplore(false));
    navigate(route);
  };

  const showCareerSection = useMemo(
    () =>
      program?.data.school.school_rankings?.[0] ||
      (program?.data.school.working_after_months &&
        program?.data.school.working_after_percentage) ||
      program?.data.school.alumni_information ||
      (program?.data.school.companies && program?.data.school.companies.length > 0) ||
      program?.data.school.career_service_description,
    [program]
  );
  const showVisaSection = useMemo(
    () =>
      (program?.data.school.city.country.working_visa_number &&
        program?.data.school.city.country.working_visa_format) ||
      program?.data.school.city.country.working_visa_after_study ||
      program?.data.school.city.country.visa_description,
    [program]
  );

  return (
    <div>
      {comesFromExplore && (
        <Helmet>
          <link
            rel="canonical"
            href={
              programSlug?.includes('&spv=1')
                ? `https://unifyapply.com/programs/${programSlug.slice(0, -6)}`
                : `https://unifyapply.com/programs/${programSlug}`
            }
          />
        </Helmet>
      )}
      {isLoading || isFetching ? (
        <Loader />
      ) : (
        <div className={comesFromExplore ? styles.exploreContainer : ''}>
          <div className={styles.desktopContainer} id="detailContainer">
            {program?.data.photos?.length ? (
              <div
                id="carouselExampleIndicators"
                className={`carousel slide`}
                data-bs-ride="carousel"
              >
                <div className="carousel-indicators">
                  {program.data.photos.length > 1 &&
                    program.data.photos.map((photo: string, index: number) => (
                      <div key={index}>
                        {index === 0 ? (
                          <button
                            type="button"
                            data-bs-target="#carouselExampleIndicators"
                            data-bs-slide-to={`${index}`}
                            className={`active ${styles.buttonDot}`}
                            aria-current="true"
                            aria-label={`Slide ${index}`}
                          ></button>
                        ) : (
                          <button
                            type="button"
                            data-bs-target="#carouselExampleIndicators"
                            data-bs-slide-to={`${index}`}
                            className={`${styles.buttonDot}`}
                            aria-label={`Slide ${index}`}
                          ></button>
                        )}
                      </div>
                    ))}
                </div>
                <div
                  className={`carousel-inner ${
                    comesFromExplore ? styles.carouselContainerExplore : styles.carouselContainer
                  }`}
                >
                  {program.data.photos.length &&
                    program.data.photos.map((photo: string, index: number) => (
                      <div className={`carousel-item ${index === 0 ? 'active' : ''}`} key={index}>
                        <img
                          src={`${photo}`}
                          className={`${styles.photo}`}
                          alt={`Photo ${index + 1} of ${program.data.name}`}
                        />
                      </div>
                    ))}
                </div>
                {program.data.photos.length > 1 && (
                  <div>
                    <button
                      className={`carousel-control-prev ${styles.carouselButtons}`}
                      type="button"
                      data-bs-target="#carouselExampleIndicators"
                      data-bs-slide="prev"
                    >
                      <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                      <span className="visually-hidden">Previous</span>
                    </button>
                    <button
                      className={`carousel-control-next ${styles.carouselButtons}`}
                      type="button"
                      data-bs-target="#carouselExampleIndicators"
                      data-bs-slide="next"
                    >
                      <span className="carousel-control-next-icon" aria-hidden="true"></span>
                      <span className="visually-hidden">Next</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.placeholderPhoto}>
                <div>
                  <img src={Placeholder} className={`${styles.f}`} alt="placeholder" />
                </div>
              </div>
            )}
            {isMobile && (
              <>
                <div className={styles.headerContainer} ref={headerRef}>
                  <div className="d-flex flex-row align-items-center mt-4 ms-3 gap-2">
                    <img
                      src={program?.data.school.logo}
                      alt={`Logo of ${program?.data.school.name}`}
                      className={styles.logoContainer}
                    />
                    <div className={styles.programTitle}>
                      <h1 className={styles.titleText}>{program?.data.name}</h1>
                      <div className={styles.countryInfo}>
                        <Text size={14} weight={600}>
                          {`${program?.data.school.name}, ${program?.data.school.city.country.name}`}
                        </Text>
                        <Flag
                          code={program?.data.school.city.country.code}
                          className={styles.countryFlag}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="d-flex flex-row align-items-center gap-4 mt-4 ms-3">
                    {program?.data?.school.school_rankings?.[0] && (
                      <div className={styles.rankingSection}>
                        <div className={styles.blockNumber}>
                          {program.data.school.school_rankings[0].score}
                        </div>
                        <div className={styles.rankingInfo}>
                          <p className={`${textStyles['text-primary-12-600']}`}>
                            {program.data.school.school_rankings[0].ranking.name}
                          </p>
                          <p className={`${textStyles['text-primary-12-600']}`}>
                            {`(${program.data.school.school_rankings[0].ranking.year})`}
                          </p>
                        </div>
                      </div>
                    )}
                    {program?.data?.school.school_accreditations?.[0] && (
                      <div className={styles.rankingSection}>
                        <div className={styles.blockNumber}>
                          <img
                            src={program.data.school.school_accreditations[0].accreditation.logo}
                            alt={`Logo of ${program.data.school.school_accreditations[0].accreditation.name}`}
                            className={styles.accLogoContainer}
                          />
                        </div>
                        <div className={styles.rankingInfo}>
                          <p className={`${textStyles['text-primary-12-600']}`}>
                            {`${program.data.school.school_accreditations[0].accreditation.name}-Accredited School`}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  {program?.data?.discounts?.[0] && (
                    <div className="d-flex flex-column mt-4 gap-2">
                      {program.data.discounts.map((discount: Discount) => (
                        <div className={styles.discountContainer} key={discount.id}>
                          <Text size={isMobile ? 11 : 14} weight={500}>
                            {discount.percentage_value === 1 &&
                              discount.kind == 'application_fee' && <del>Application fee £100</del>}
                            &nbsp;
                            {discount.description}
                          </Text>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    className={`btn btn-primary btn-lg ${styles.favoriteButton}`}
                    onClick={() => (isFavorite ? handleDeleteFavorite() : handleAddFavorite())}
                  >
                    Shortlist
                    {isFavorite ? (
                      <div className={styles.big_heart_fill}>
                        <i className="bi bi-heart-fill"></i>
                      </div>
                    ) : (
                      <div className={styles.big_heart}>
                        <i className="bi bi-heart"></i>
                      </div>
                    )}
                  </button>
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={() =>
                      window.open(
                        `https://unifyapply.com/experts/request-meeting?program_id=${program?.data.id}&program_name=${program?.data.name}`
                      )
                    }
                  >
                    Ask expert for help
                  </button>
                </div>
                {!headerInView && (
                  <div className={styles.fixedProgramTitle}>
                    <div className={styles.programInfo}>
                      <img
                        src={program?.data.school.logo}
                        className={styles.logoContainer}
                        alt={`Logo of ${program?.data.school.name}`}
                      />
                      <p className={styles.programName}>{program?.data.name}</p>
                    </div>
                    <button
                      onClick={() =>
                        window.open(
                          `https://unifyapply.com/experts/request-meeting?program_id=${program?.data.id}&program_name=${program?.data.name}`
                        )
                      }
                    >
                      Ask expert
                    </button>
                  </div>
                )}
              </>
            )}
            {!isMobile ? (
              <div className={comesFromExplore ? '' : 'row'}>
                <div className={comesFromExplore ? 'col-12' : 'col-8'}>
                  <div style={{ paddingTop: '40px' }}></div>
                  <div className={`${styles.section}`}>
                    <img
                      src={program?.data.school.logo}
                      alt={`Logo of ${program?.data.school.name}`}
                      className={styles.logoContainerDT}
                    />
                    <div className="d-flex flex-column">
                      {comesFromExplore ? (
                        <h2 className={`${textStyles['text-primary-30-700']}`}>
                          {program?.data.name}
                        </h2>
                      ) : (
                        <h1 className={`${textStyles['text-primary-30-700']}`}>
                          {program?.data.name}
                        </h1>
                      )}
                      <div className={styles.countryInfo}>
                        <Text size={20} weight={600}>
                          {`${program?.data.school.name}, ${program?.data.school.city.country.name}`}
                        </Text>
                        <Flag
                          code={program?.data.school.city.country.code}
                          className={styles.countryFlag}
                        />
                      </div>
                      {program?.data?.discounts?.[0] && (
                        <div className="d-flex flex-row mt-4 gap-2">
                          {program.data.discounts.map((discount: Discount) => (
                            <div className={styles.discountContainer} key={discount.id}>
                              <Text size={isMobile ? 11 : 14} weight={500}>
                                {discount.percentage_value === 1 &&
                                  discount.kind == 'application_fee' && (
                                    <del>Application fee £100</del>
                                  )}
                                &nbsp;
                                {discount.description}
                              </Text>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className={styles.rankingContainer}>
                      {comesFromExplore && program?.data?.school.school_rankings?.[0] && (
                        <div className={styles.rankingSection}>
                          <div className={styles.blockNumber}>
                            {program.data.school.school_rankings[0].score}
                          </div>
                          <div className={styles.rankingInfo}>
                            <p className={`${textStyles['text-primary-14-600']}`}>
                              {program.data.school.school_rankings[0].ranking.name}
                            </p>
                            <p className={`${textStyles['text-primary-14-600']}`}>
                              {`(${program.data.school.school_rankings[0].ranking.year})`}
                            </p>
                          </div>
                        </div>
                      )}
                      {comesFromExplore && program?.data?.school.school_accreditations?.[0] && (
                        <div className={styles.rankingSection}>
                          <div className={styles.blockNumber}>
                            <img
                              src={program.data.school.school_accreditations[0].accreditation.logo}
                              alt={`Logo of ${program.data.school.school_accreditations[0].accreditation.name}`}
                              className={styles.accLogoContainer}
                            />
                          </div>
                          <div className={styles.rankingInfo}>
                            <p className={`${textStyles['text-primary-14-600']}`}>
                              {`${program.data.school.school_accreditations[0].accreditation.name}-Accredited School`}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {comesFromExplore && (
                    <div className={`d-flex flex-row gap-3 ${styles.exploreButtons}`}>
                      <a
                        type="button"
                        className="btn btn-primary btn-lg"
                        href={`https://unifyapply.com/experts/request-meeting?program_id=${program?.data.id}&program_name=${program?.data.name}`}
                      >
                        Ask expert for help
                      </a>
                      <button
                        className={styles.detailButton}
                        onClick={() => {
                          navigateTo(
                            newDetailUrl('programs', program?.data.id, program?.data.name)
                          );
                        }}
                      >
                        Read more
                      </button>
                    </div>
                  )}
                  <div className={styles.infoContainer}>
                    <OverviewComponent
                      entity={program?.data}
                      overviewRef={overviewRef}
                      comesFromExplore={comesFromExplore}
                    />
                    <MatchingSection entity={program} handleApply={handleApply} />
                    {!!(
                      (program?.data.labels && program?.data.labels.length > 0) ||
                      (program?.data.school.labels && program?.data.school.labels.length > 0)
                    ) && <TagsSection entity={program?.data} />}
                    {!!program?.data.school.contact_banner && <ContactBanner entity={program} />}
                    {!comesFromExplore ? (
                      <>
                        <ProgramProfileSection
                          program={program}
                          comesFromExplore={comesFromExplore}
                          programProfileRef={programProfileRef}
                        />
                        <TeachingSection entity={program} />
                        <ApplySection entity={program} applyRef={applyRef} />
                        <CostsSection program={program} costsRef={costsRef} />
                        {showCareerSection && (
                          <CareerSection entity={program} careerRef={careerRef} />
                        )}
                        {showVisaSection && <VisaSection entity={program} visaRef={visaRef} />}
                        <StudentLife entity={program} studentLifeRef={studentLifeRef} />
                        <FAQSection faqRef={faqRef} entity={program?.data} />
                      </>
                    ) : (
                      <DetailSections entity={program?.data} />
                    )}
                    <SimilarPrograms
                      programId={parseInt(programId || '')}
                      similarProgramsRef={similarProgramsRef}
                      comesFromExplore={comesFromExplore}
                    />
                    {comesFromExplore ? (
                      <JourneyBanner logged={logged} navigateTo={navigateTo} isMobile={isMobile} />
                    ) : (
                      <ProfilingBanner />
                    )}
                    {!comesFromExplore && (
                      <>
                        <OverviewComponent
                          entity={program?.data.school}
                          overviewRef={schoolOverviewRef}
                          comesFromExplore={comesFromExplore}
                          belongsToProgramSchool={true}
                        />
                        {!!(
                          program?.data.school.labels && program?.data.school.labels.length > 0
                        ) && <TagsSection entity={program?.data.school} />}
                        {(program?.data.school.international_student_rate ||
                          program?.data.school.number_of_students ||
                          program?.data.school.international_student_description) && (
                          <InternationalStudentsComponent
                            school={program?.data.school}
                            comesFromExplore={comesFromExplore}
                          />
                        )}
                        <DetailSections
                          entity={program?.data.school}
                          belongsToProgramSchool={true}
                        />
                        {!!program?.data.school.contact_banner && (
                          <ContactBanner entity={program} />
                        )}
                      </>
                    )}
                  </div>
                  <div style={{ paddingTop: 50 }}></div>
                </div>
                {!comesFromExplore && (
                  <div className={`col-4 ${styles.sideColumn}`}>
                    {program?.data?.school.school_rankings?.[0] && (
                      <div className={styles.rankingSection}>
                        <div className={styles.blockNumber}>
                          {program.data.school.school_rankings[0].score}
                        </div>
                        <div className={styles.rankingInfo}>
                          <p className={`${textStyles['text-primary-18-600']}`}>
                            {program.data.school.school_rankings[0].ranking.name}
                          </p>
                          <p className={`${textStyles['text-primary-18-600']}`}>
                            {`(${program.data.school.school_rankings[0].ranking.year})`}
                          </p>
                        </div>
                      </div>
                    )}
                    {program?.data?.school.school_accreditations?.[0] && (
                      <div className={styles.rankingSection}>
                        <div className={styles.blockNumber}>
                          <img
                            src={program.data.school.school_accreditations[0].accreditation.logo}
                            alt={`Logo of ${program.data.school.school_accreditations[0].accreditation.name}`}
                            className={styles.accLogoContainer}
                          />
                        </div>
                        <div className={styles.rankingInfo}>
                          <p className={`${textStyles['text-primary-18-600']}`}>
                            {`${program.data.school.school_accreditations[0].accreditation.name}-Accredited School`}
                          </p>
                        </div>
                      </div>
                    )}
                    <TabsComponent
                      program={program || undefined}
                      programTopic={activeProgramTopic}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div>
                <TabsComponent program={program || undefined} programTopic={activeProgramTopic} />
                <OverviewComponent
                  entity={program?.data}
                  overviewRef={overviewRef}
                  comesFromExplore={comesFromExplore}
                />
                <MatchingSection entity={program} handleApply={handleApply} />
                {!!(
                  (program?.data.labels && program?.data.labels.length > 0) ||
                  (program?.data.school.labels && program?.data.school.labels.length > 0)
                ) && <TagsSection entity={program?.data} />}
                {!!program?.data.school.contact_banner && <ContactBanner entity={program} />}
                <ProgramProfileSection
                  program={program}
                  comesFromExplore={comesFromExplore}
                  programProfileRef={programProfileRef}
                />
                <TeachingSection entity={program} />
                <ApplySection entity={program} applyRef={applyRef} />
                <CostsSection program={program} costsRef={costsRef} />
                {showCareerSection && <CareerSection entity={program} careerRef={careerRef} />}
                {showVisaSection && <VisaSection entity={program} visaRef={visaRef} />}
                <StudentLife entity={program} studentLifeRef={studentLifeRef} />
                <FAQSection faqRef={faqRef} entity={program?.data} />
                <SimilarPrograms
                  programId={parseInt(programId || '')}
                  similarProgramsRef={similarProgramsRef}
                />
                <ProfilingBanner />
                <OverviewComponent
                  entity={program?.data.school}
                  overviewRef={schoolOverviewRef}
                  comesFromExplore={comesFromExplore}
                  belongsToProgramSchool={true}
                />
                {!!(program?.data.school.labels && program?.data.school.labels.length > 0) && (
                  <TagsSection entity={program?.data.school} />
                )}
                {(program?.data.school.international_student_rate ||
                  program?.data.school.number_of_students ||
                  program?.data.school.international_student_description) && (
                  <InternationalStudentsComponent
                    school={program?.data.school}
                    comesFromExplore={comesFromExplore}
                  />
                )}
                <DetailSections entity={program?.data.school} belongsToProgramSchool={true} />
                {!!program?.data.school.contact_banner && <ContactBanner entity={program} />}
                <div style={{ paddingTop: 50 }}></div>
              </div>
            )}
          </div>
          {/* {!inView && !comesFromExplore && (
            <div className={`${styles.bottomBar} fade-in-up`}>
              <div className={styles.programBox}>
                <img
                  src={program?.data.school.logo}
                  alt={`Logo of ${program?.data.name}`}
                  className={styles.logoBar}
                />
                <div>
                  <div className={styles.program}>{program?.data.name}</div>
                  <div className={styles.school}>{program?.data.school.name}</div>
                </div>
              </div>
              <div>
                <a href={applyLink}>
                  <button className={`btn btn-light`}>Get more Info</button>
                </a>
              </div>
            </div>
          )} */}
        </div>
      )}
    </div>
  );
};

export default ProgramDetail;
